<template>
  <div class="user-review-summary">
    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="page-header">
          <div>
            <div class="kicker">{{ text.kicker }}</div>
            <h2>{{ isViewMode ? text.viewTitle : text.reviewTitle }}</h2>
            <p>{{ isViewMode ? text.viewCopy : text.reviewCopy }}</p>
          </div>
        </div>

        <div v-if="agreement" class="summary-grid">
          <div class="summary-card summary-card--agreement">
            <div class="section-head">
              <div class="section-kicker">{{ text.sections.agreement }}</div>
              <h3>{{ text.sections.coreDetails }}</h3>
            </div>
            <div class="detail-list">
              <div class="detail-row"><span>{{ text.fields.title }}</span><strong>{{ agreement.title }}</strong></div>
              <div class="detail-row"><span>{{ text.fields.type }}</span><strong>{{ agreement.activityType }}</strong></div>
              <div class="detail-row"><span>{{ text.fields.partner }}</span><strong>{{ agreement.partner }}</strong></div>
              <div class="detail-row"><span>{{ text.fields.country }}</span><strong>{{ agreement.country }}</strong></div>
              <div class="detail-row"><span>{{ text.fields.status }}</span><strong>{{ agreement.derivedStageLabel }}</strong></div>
              <div v-if="agreementSummary" class="detail-row detail-row--note"><span>{{ text.fields.summary }}</span><strong>{{ agreementSummary }}</strong></div>
            </div>
          </div>

          <div class="summary-card summary-card--clauses">
            <div class="section-head">
              <div class="section-kicker">{{ text.sections.clauses }}</div>
              <h3>{{ text.sections.selectedClauses }}</h3>
            </div>
            <div v-if="agreement.selectedClauses && agreement.selectedClauses.length" class="clause-cloud">
              <div class="clause-chip" v-for="item in agreement.selectedClauses" :key="item.id || item._id || item.title">{{ item.title }}</div>
            </div>
            <div v-else class="muted">{{ text.noClauses }}</div>
          </div>
        </div>

        <div v-if="agreement" class="preview-card">
          <h3>{{ text.previewTitle }}</h3>
          <AgreementDocumentHeader
            class="preview-card__document-header"
            :title="agreement.title"
            :partner="agreement.partner"
            :partner-logo="agreement.partnerLogo"
            :agreement-type="agreement.activityType"
            :country="agreement.country"
            title-label="Agreement Title"
            compact
            :show-meta="true"
          />
          <div v-if="isUploadedDraft" class="draft-upload-preview">
            <div class="draft-upload-preview__meta">
              <strong>{{ agreement.uploadedDraft && agreement.uploadedDraft.name ? agreement.uploadedDraft.name : text.uploadedDraftDocument }}</strong>
              <span>{{ agreement.uploadedDraft && agreement.uploadedDraft.sizeLabel ? agreement.uploadedDraft.sizeLabel : text.uploadedDraftMeta }}</span>
            </div>
            <div v-if="uploadedDraftUrl || canDownloadAgreementDocx || canDownloadAgreementPdf" class="draft-upload-preview__actions">
              <a v-if="uploadedDraftUrl" :href="uploadedDraftUrl" target="_blank" rel="noopener" class="draft-upload-preview__link">{{ text.openWord }}</a>
              <button v-if="canDownloadAgreementDocx" class="draft-upload-preview__link draft-upload-preview__link--button" type="button" @click="downloadCompletedAgreement('docx')">{{ text.downloadDocx }}</button>
              <button v-if="canDownloadAgreementPdf" class="draft-upload-preview__link draft-upload-preview__link--button" type="button" @click="downloadCompletedAgreement('pdf')">{{ text.downloadPdf }}</button>
            </div>
            <div v-else class="muted">{{ text.noDownloadUrl }}</div>
            <div v-if="previewSections.length" class="draft-builder-preview">
              <div
                v-for="item in previewSections"
                :key="item.key"
                class="preview-section"
              >
                <div v-if="item.label" class="preview-section__label">{{ item.label }}</div>
                <AgreementSignaturePreview
                  v-if="isSignaturePreviewSection(item)"
                  :section="item"
                  left-header="Mae Fah Luang University"
                  :right-header="[agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join('\n')"
                />
                <p v-else :class="{ 'preview-section__content--centered': isCenteredPreviewSection(item) }">{{ item.content }}</p>
              </div>
            </div>
          </div>

          <div v-else class="draft-builder-preview">
            <div
              v-for="item in previewSections"
              :key="item.key"
              class="preview-section"
            >
              <div v-if="item.label" class="preview-section__label">{{ item.label }}</div>
              <AgreementSignaturePreview
                v-if="isSignaturePreviewSection(item)"
                :section="item"
                left-header="Mae Fah Luang University"
                :right-header="[agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join('\n')"
              />
              <p v-else :class="{ 'preview-section__content--centered': isCenteredPreviewSection(item) }">{{ item.content }}</p>
            </div>
            <div v-if="!previewSections.length" class="muted">{{ text.noPreview }}</div>
          </div>
        </div>

        <div class="submit-box" v-if="!isViewMode">
          <div class="submit-box__intro">
            <h3>{{ text.confirmTitle }}</h3>
          </div>
          <label class="confirm-card" :class="{ 'confirm-card--checked': confirmed }">
            <input v-model="confirmed" type="checkbox" />
            <span class="confirm-card__content">
              <strong>{{ text.confirmSubmit }}</strong>
              <small>{{ confirmed ? text.confirmCheckedHint : text.confirmHint }}</small>
            </span>
          </label>
          <div class="actions">
            <CButton color="secondary" variant="outline" class="mr-2" @click="goBack">{{ text.back }}</CButton>
            <CButton color="primary" :disabled="!confirmed" @click="submitAgreement">{{ text.submitAgreement }}</CButton>
          </div>
        </div>

        <div class="actions" v-else>
          <CButton v-if="canDownloadAgreementDocx" color="primary" class="mr-2" @click="downloadCompletedAgreement('docx')">{{ text.downloadDocx }}</CButton>
          <CButton v-if="canDownloadAgreementPdf" color="secondary" variant="outline" class="mr-2" @click="downloadCompletedAgreement('pdf')">{{ text.downloadPdf }}</CButton>
          <CButton color="secondary" variant="outline" @click="$router.push('/dashboard/user-agreements')">{{ text.backToAgreements }}</CButton>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Service from '@/service/api'
import {
  getAgreementSectionDisplayTitle,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'
import { downloadAgreementAs } from '@/utils/agreement-download'
import { createWorkflowNotification } from '@/utils/workflow-notifications'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import AgreementSignaturePreview from '@/views/shared/AgreementSignaturePreview'

const PAGE_TEXT = {
  en: {
    kicker: 'Review and Submit',
    viewTitle: 'Agreement Details',
    reviewTitle: 'Review Summary',
    viewCopy: 'View the submitted agreement and its current workflow stage in read-only mode.',
    reviewCopy: 'Confirm the agreement details, then submit it so the committee review can begin immediately.',
    sections: {
      agreement: 'Agreement',
      coreDetails: 'Core Details',
      clauses: 'Clauses',
      selectedClauses: 'Selected Clauses'
    },
    fields: { title: 'Title', type: 'Type', partner: 'Partner', country: 'Country', status: 'Status', summary: 'Additional Note' },
    noClauses: 'No clauses selected.',
    previewTitle: 'Draft Preview',
    uploadedDraftDocument: 'Uploaded Draft Document',
    uploadedDraftMeta: 'DOC or DOCX uploaded by the user',
    openWord: 'Open uploaded document',
    downloadDocx: 'Download DOCX',
    downloadPdf: 'Download PDF',
    noDownloadUrl: 'This uploaded document does not have a downloadable file URL yet.',
    noPreview: 'No draft preview is available yet.',
    confirmTitle: 'Confirm before sending to committee review',
    confirmSubmit: 'I reviewed this agreement and want to send it to committee review.',
    confirmHint: 'Required to enable the Submit Agreement button.',
    confirmCheckedHint: 'Confirmed. You can submit this agreement now.',
    back: 'Back',
    submitAgreement: 'Submit Agreement',
    backToAgreements: 'Back to Agreements',
    articleLabel: 'Article',
    draftDocument: 'Draft Document',
    agreementSubmittedNote: 'Agreement submitted by user.',
    notification: {
      adminTitle: 'Agreement submitted for committee review',
      adminMessage: (title) => `${title || 'An agreement'} was submitted by the user and has moved into committee review.`,
      adminSource: 'Review Queue',
      adminAction: 'Open review',
      adminNextStep: 'Monitor the committee review and complete final approval after committee submission.',
      committeeTitle: 'New agreement ready for committee review',
      committeeMessage: (title) => `${title || 'An agreement'} was submitted and is ready for committee review.`,
      committeeSource: 'Committee Reviews',
      committeeAction: 'Start review',
      committeeNextStep: 'Open the agreement and begin the committee review.'
    }
  },
  th: {
    kicker: 'ตรวจสอบและส่ง',
    viewTitle: 'รายละเอียดข้อตกลง',
    reviewTitle: 'สรุปก่อนส่งรีวิว',
    viewCopy: 'ดูข้อตกลงที่ส่งแล้วและสถานะเวิร์กโฟลว์ปัจจุบันในโหมดอ่านอย่างเดียว',
    reviewCopy: 'ยืนยันรายละเอียดข้อตกลง แล้วส่งเพื่อเริ่มกระบวนการรีวิวของคณะกรรมการทันที',
    sections: {
      agreement: 'ข้อตกลง',
      coreDetails: 'รายละเอียดหลัก',
      clauses: 'ข้อกำหนด',
      selectedClauses: 'ข้อกำหนดที่เลือก'
    },
    fields: { title: 'ชื่อ', type: 'ประเภท', partner: 'คู่สัญญา', country: 'ประเทศ', status: 'สถานะ', summary: 'หมายเหตุเพิ่มเติม' },
    noClauses: 'ยังไม่ได้เลือกข้อกำหนด',
    previewTitle: 'ตัวอย่างร่างเอกสาร',
    uploadedDraftDocument: 'เอกสารร่างที่อัปโหลด',
    uploadedDraftMeta: 'ไฟล์ DOC หรือ DOCX ที่ผู้ใช้อัปโหลด',
    openWord: 'เปิดเอกสารที่อัปโหลด',
    downloadDocx: 'ดาวน์โหลด DOCX',
    downloadPdf: 'ดาวน์โหลด PDF',
    noDownloadUrl: 'เอกสารที่อัปโหลดนี้ยังไม่มีลิงก์สำหรับดาวน์โหลด',
    noPreview: 'ยังไม่มีตัวอย่างร่างเอกสาร',
    confirmTitle: 'ยืนยันก่อนส่งเข้าสู่การรีวิวของคณะกรรมการ',
    confirmSubmit: 'ฉันได้ตรวจสอบข้อตกลงนี้แล้ว และต้องการส่งเข้าสู่การรีวิวของคณะกรรมการ',
    confirmHint: 'ต้องยืนยันก่อนจึงจะกดปุ่มส่งข้อตกลงได้',
    confirmCheckedHint: 'ยืนยันแล้ว ขณะนี้สามารถส่งข้อตกลงได้',
    back: 'ย้อนกลับ',
    submitAgreement: 'ส่งข้อตกลง',
    backToAgreements: 'กลับไปที่ข้อตกลงของฉัน',
    articleLabel: 'มาตรา',
    draftDocument: 'เอกสารร่าง',
    agreementSubmittedNote: 'ผู้ใช้ส่งข้อตกลงแล้ว',
    notification: {
      adminTitle: 'มีการส่งข้อตกลงเข้าสู่การรีวิวของคณะกรรมการ',
      adminMessage: (title) => `${title || 'มีข้อตกลงหนึ่งฉบับ'} ถูกส่งโดยผู้ใช้และย้ายเข้าสู่ขั้นรีวิวของคณะกรรมการแล้ว`,
      adminSource: 'คิวรีวิว',
      adminAction: 'เปิดรีวิว',
      adminNextStep: 'ติดตามการรีวิวของคณะกรรมการและดำเนินการอนุมัติขั้นสุดท้ายหลังจากคณะกรรมการส่งผลกลับมาแล้ว',
      committeeTitle: 'มีข้อตกลงใหม่พร้อมให้คณะกรรมการรีวิว',
      committeeMessage: (title) => `${title || 'มีข้อตกลงหนึ่งฉบับ'} ถูกส่งแล้วและพร้อมสำหรับการรีวิวของคณะกรรมการ`,
      committeeSource: 'งานรีวิวของคณะกรรมการ',
      committeeAction: 'เริ่มรีวิว',
      committeeNextStep: 'เปิดข้อตกลงและเริ่มการรีวิวของคณะกรรมการ'
    }
  }
}

export default {
  name: 'UserReviewSummary',
  components: {
    AgreementDocumentHeader,
    AgreementSignaturePreview
  },
  data() {
    return {
      agreement: null,
      confirmed: false
    }
  },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    agreementId() {
      return this.$route.params.id
    },
    isViewMode() {
      return this.$route.query && this.$route.query.mode === 'view'
    },
    isUploadedDraft() {
      return !!(this.agreement && this.agreement.draftSource === 'upload')
    },
    agreementSummary() {
      return String(this.agreement && this.agreement.summary ? this.agreement.summary : '').trim()
    },
    uploadedDraftUrl() {
      if (this.agreement && this.agreement.finalDocument && this.agreement.finalDocument.url) {
        return this.agreement.finalDocument.url
      }
      return this.agreement && this.agreement.uploadedDraft && this.agreement.uploadedDraft.url
        ? this.agreement.uploadedDraft.url
        : ''
    },
    previewSections() {
      if (!this.agreement) return []
      const draftPreviewSections = normalizeAgreementSections(this.agreement.draftPreviewSections)
      if (draftPreviewSections.length) {
        return draftPreviewSections.map((item, index) => ({
          key: item && item.key ? item.key : `section-${index + 1}`,
          label: getAgreementSectionDisplayTitle(item),
          content: item && item.content ? item.content : ''
        })).filter(item => item.content)
      }
      const drafts = normalizeAgreementSections(this.agreement.articleDrafts)
      if (drafts.length) {
        return drafts.map((item, index) => ({
          key: item && item.key ? item.key : `article-${index + 1}`,
          label: getAgreementSectionDisplayTitle(item),
          content: item && item.content ? item.content : ''
        })).filter(item => item.content)
      }
      if (this.agreement.customDocument && this.agreement.customDocument.content) {
        return [{
          key: 'custom-document',
          label: this.agreement.customDocument.title || this.text.draftDocument,
          content: this.agreement.customDocument.content
        }]
      }
      return []
    },
    downloadUrl() {
      if (this.agreement && this.agreement.finalDocument && this.agreement.finalDocument.url) {
        return this.agreement.finalDocument.url
      }
      return this.agreement && this.agreement.uploadedDraft && this.agreement.uploadedDraft.url
        ? this.agreement.uploadedDraft.url
        : ''
    },
    canDownloadAgreementDocx() {
      return !!(this.agreement && (this.previewSections.length || this.downloadUrl))
    },
    canDownloadAgreementPdf() {
      return !!(this.agreement && this.previewSections.length)
    }
  },
  mounted() {
    this.loadAgreement()
  },
  methods: {
    editorRoute() {
      const type = String(this.agreement && this.agreement.activityType ? this.agreement.activityType : 'Exchange Student').toLowerCase()
      if (type === 'double degree') return `/dashboard/user-agreements/${this.agreementId}/double-degree`
      if (type === 'extension agreement') return `/dashboard/user-agreements/${this.agreementId}/extension`
      if (type === 'mou file') return `/dashboard/user-agreements/${this.agreementId}/mou`
      return `/dashboard/user-agreements/${this.agreementId}/exchange`
    },
    async loadAgreement() {
      const response = await Service.agreements('detail', { id: this.agreementId })
      this.agreement = response && response.data && response.data.data ? response.data.data : null
    },
    goBack() {
      this.$router.push(this.editorRoute())
    },
    async submitAgreement() {
      await Service.agreements('submit', { id: this.agreementId, responseNote: this.text.agreementSubmittedNote })
      const dueAt = new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString()
      createWorkflowNotification({
        role: 'admin',
        type: 'approval',
        title: this.text.notification.adminTitle,
        message: this.text.notification.adminMessage(this.agreement && this.agreement.title),
        source: this.text.notification.adminSource,
        reference: this.agreement ? `MOU-${this.agreement.id}` : '',
        priority: 'high',
        targetPath: this.agreement ? `/review?agreementId=${this.agreement.id}` : '/review-queue',
        actionLabel: this.text.notification.adminAction,
        nextStep: this.text.notification.adminNextStep
      })
      createWorkflowNotification({
        role: 'committee',
        type: 'review',
        title: this.text.notification.committeeTitle,
        message: this.text.notification.committeeMessage(this.agreement && this.agreement.title),
        source: this.text.notification.committeeSource,
        reference: this.agreement ? `MOU-${this.agreement.id}` : '',
        priority: 'high',
        dueAt,
        targetPath: this.agreement ? `/review?agreementId=${this.agreement.id}` : '/review',
        actionLabel: this.text.notification.committeeAction,
        nextStep: this.text.notification.committeeNextStep
      })
      this.$router.push({
        path: '/dashboard/user-review-queue',
        query: {
          refresh: String(Date.now())
        }
      })
    },
    downloadCompletedAgreement(format = 'docx') {
      downloadAgreementAs(this.agreement, this.previewSections, format)
    },
    isCenteredPreviewSection(section) {
      return isCenteredAgreementSection(section, this.agreement && this.agreement.activityType)
    },
    isSignaturePreviewSection(section) {
      return isSignatureAgreementSection(section)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-review-summary");

.shell { border-radius: 20px; }
.kicker { color: #8c1515; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.page-header h2 { color: #344767; margin: 6px 0; }
.page-header p, .muted { color: #718096; }
.preview-card__document-header { margin: 12px 0 18px; }

.summary-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: minmax(0, 1.08fr) minmax(0, 1fr);
  margin-top: 1.25rem;
}

.summary-card {
  background:
    radial-gradient(circle at top right, rgba(255, 244, 223, 0.7), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(148, 163, 184, 0.07);
  padding: 18px;
}

.preview-card { border: 1px solid #e6ebf1; border-radius: 18px; margin-top: 16px; padding: 18px; }

.section-head {
  margin-bottom: 0.8rem;
}

.section-kicker {
  color: #8c1515;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
}

.summary-card h3 {
  color: #344767;
  font-size: 1.2rem;
  line-height: 1.2;
  margin: 0;
}

.preview-card h3 { color: #344767; margin: 0 0 14px; }

.detail-list {
  display: grid;
  gap: 0;
}

.detail-row {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #edf2f7;
  display: grid;
  gap: 1rem;
  grid-template-columns: 110px minmax(0, 1fr);
  padding: 0.9rem 0;
}

.detail-row span {
  color: #718096;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.detail-row strong {
  color: #344767;
  font-size: 0.98rem;
  justify-self: start;
  line-height: 1.4;
}

.detail-row--note {
  align-items: start;
}

.detail-row--note strong {
  white-space: pre-wrap;
}

.detail-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.clause-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.clause-chip {
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  border: 1px solid #e7edf4;
  border-radius: 999px;
  color: #344767;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 700;
  min-height: 2.5rem;
  padding: 0.65rem 1rem;
}

.draft-upload-preview__meta { display: grid; gap: 4px; margin-bottom: 14px; }
.draft-upload-preview__meta strong { color: #344767; }
.draft-upload-preview__meta span { color: #718096; }
.draft-upload-preview__actions { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.draft-upload-preview__link { color: #8c1515; font-weight: 700; }
.draft-upload-preview__link--button { background: transparent; border: 0; cursor: pointer; padding: 0; }
.preview-section + .preview-section { margin-top: 16px; }
.preview-section__label { color: #344767; font-size: 0.82rem; font-weight: 700; margin-bottom: 6px; text-transform: uppercase; }
.preview-section p { color: #52606d; line-height: 1.65; margin: 0; white-space: pre-wrap; }
.preview-section__content--centered { text-align: center; }
.preview-signature-columns {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.preview-signature-column {
  background: #fbfcfe;
  border: 1px solid #e6ebf1;
  border-radius: 14px;
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 14px;
}
.preview-signature-line {
  color: #52606d;
  line-height: 1.6;
  white-space: pre-wrap;
}
.submit-box {
  background: #ffffff;
  border: 1px solid #e6ebf1;
  border-radius: 20px;
  margin-top: 20px;
  padding: 20px;
}

.submit-box__intro {
  margin-bottom: 16px;
}

.submit-box__intro h3 {
  color: #344767;
  font-size: 1.1rem;
  margin: 0;
}

.confirm-card {
  align-items: flex-start;
  background: #ffffff;
  border: 2px solid #e8dccd;
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  padding: 16px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.confirm-card:hover {
  border-color: #d4b08a;
  box-shadow: 0 10px 24px rgba(148, 163, 184, 0.08);
}

.confirm-card input {
  accent-color: #8c1515;
  flex: 0 0 auto;
  height: 20px;
  margin-top: 2px;
  width: 20px;
}

.confirm-card--checked {
  border-color: #8c1515;
  box-shadow: 0 12px 28px rgba(140, 21, 21, 0.12);
}

.confirm-card__content {
  display: grid;
  gap: 4px;
}

.confirm-card__content strong {
  color: #344767;
  font-size: 0.98rem;
  line-height: 1.45;
}

.confirm-card__content small {
  color: #9a3412;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.35;
}

.actions { display: flex; justify-content: flex-end; }

@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .preview-signature-columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .detail-row {
    grid-template-columns: 1fr;
  }

  .summary-card h3 {
    font-size: 1.05rem;
  }

  .submit-box {
    padding: 16px;
  }

  .actions {
    justify-content: flex-start;
  }
}
</style>
